import { SearchFilter } from 'components/ui/SearchFilter';
import { getFilteredList } from 'functions/misc';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rq } from 'http/apiRoutes';
import { useGet } from 'http/useApi';
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import PeopleAltTwoToneIcon from '@mui/icons-material/PeopleAltTwoTone';
import { Grid, List, Paper } from '@mui/material';
import { SingleDepartmentView } from 'components/ui/SingleDepartmentView';
import { useTranslation } from 'react-i18next';

import { IDepartmentViewModel } from 'Interfaces/ICompaniesConfig';
import { isEmptyObject } from 'functions/isEmpotyObject';
import _ from 'lodash';

export const Departments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { height } = useWindowsDimension();

  // State for filtered departments list
  const [departments, setDepartments] = useState<IDepartmentViewModel[]>([]);

  // Fetch departments for the given company
  let isEnabled = true;
  const { data } = useGet(rq.departments(companyId!, isEnabled));

  // Navigate to "new department" form
  const handleNew = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate(`/companies/${companyId}/departments/new`);
  };

  // Filter departments by search input
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setDepartments(getFilteredList(data?.departments, query));
  };

  // When data loads or updates, clone it into local state
  useEffect(() => {
    if (data?.departments) {
      const cloned = _.cloneDeep<IDepartmentViewModel[]>(data.departments);
      setDepartments(cloned);
      let id = cloned[0]?.id;
      navigate(`/companies/${companyId}/departments/${id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Grid
      container
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
      }}
    >
      <Grid item xs={6} sx={{ pr: 0.5, m: 0 }}>
        <SearchFilter
          Icon={PeopleAltTwoToneIcon}
          Header={t('department.listHeader')}
          handleSearch={handleSearch}
          handleNew={handleNew}
        />
        <List
          sx={{
            height: Math.ceil(height - 244),
            width: '100%',
            mb: 0,
            p: 2,
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {!isEmptyObject(departments) &&
            departments.map((dep, index) => (
              <Paper
                key={dep.id}
                elevation={1}
                sx={{ mb: 1 }}
                onClick={() =>
                  navigate(`/companies/${companyId}/departments/${dep.id}`)
                }
              >
                <SingleDepartmentView index={index} department={dep} />
              </Paper>
            ))}
        </List>

        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>

      <Grid item xs={6} sx={{ m: 0, pl: 0.5 }}>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: Math.ceil(height - 128),
          }}
        >
          <Outlet />
        </Paper>
      </Grid>
    </Grid>
  );
};
